import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PreEmployment() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ preferred_name: "", date_of_birth: "", phone: "", address: "", ni_number: "", emergency_contact: "", emergency_relationship: "", bank_details: "", right_to_work: "", medical_notes: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get(`/preemployment/${token}`).then((r) => {
      setInfo(r.data);
      if (r.data.details) setF((s) => ({ ...s, ...r.data.details }));
      if (r.data.status === "completed") setDone(true);
    }).catch(() => setErr(true));
  }, [token]);

  const send = async (save_only) => {
    setSaving(true);
    try {
      await api.post(`/preemployment/${token}`, { ...f, save_only });
      if (save_only) toast.success("Progress saved");
      else setDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center text-slate-500">This onboarding link is not valid.</div>;
  if (!info) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0B1A2F] py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8">
        {done ? (
          <div className="text-center py-10" data-testid="preemp-done">
            <CheckCircle2 size={48} className="text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-bold text-[#0B1A2F] mt-4">All done — welcome to CASS!</h1>
            <p className="text-slate-500 mt-2">Your new-starter details have been submitted securely. Our People team will be in touch with your first-day information.</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-[#0B1A2F]">New starter onboarding</h1>
            <p className="text-slate-500 mt-1">Welcome {info.first_name}! Please complete your details for the role of <strong>{info.job_title}</strong>. Fields marked * are required.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs text-slate-500">Preferred name</Label><Input value={f.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} data-testid="preemp-preferred" /></div>
              <div><Label className="text-xs text-slate-500">Date of birth *</Label><Input type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="preemp-dob" /></div>
              <div><Label className="text-xs text-slate-500">Phone *</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="preemp-phone" /></div>
              <div><Label className="text-xs text-slate-500">National Insurance no. *</Label><Input value={f.ni_number} onChange={(e) => set("ni_number", e.target.value)} data-testid="preemp-ni" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Home address *</Label><Textarea rows={2} value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="preemp-address" /></div>
              <div><Label className="text-xs text-slate-500">Emergency contact *</Label><Input value={f.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} data-testid="preemp-emergency" /></div>
              <div><Label className="text-xs text-slate-500">Relationship</Label><Input value={f.emergency_relationship} onChange={(e) => set("emergency_relationship", e.target.value)} data-testid="preemp-relationship" /></div>
              <div><Label className="text-xs text-slate-500">Right to work</Label><Input value={f.right_to_work} onChange={(e) => set("right_to_work", e.target.value)} placeholder="e.g. British citizen / share code" data-testid="preemp-rtw" /></div>
              <div><Label className="text-xs text-slate-500">Bank details (optional)</Label><Input value={f.bank_details} onChange={(e) => set("bank_details", e.target.value)} data-testid="preemp-bank" /></div>
              <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Medical / accessibility needs (optional)</Label><Textarea rows={2} value={f.medical_notes} onChange={(e) => set("medical_notes", e.target.value)} data-testid="preemp-medical" /></div>
            </div>
            <p className="text-[11px] text-slate-400 mt-4 flex items-center gap-1"><ShieldCheck size={13} />Your information is stored securely and only visible to authorised HR staff.</p>
            <div className="flex gap-2 mt-6">
              <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6] font-semibold" onClick={() => send(false)} disabled={saving} data-testid="preemp-submit">{saving ? "Submitting…" : "Submit details"}</Button>
              <Button variant="outline" onClick={() => send(true)} disabled={saving} data-testid="preemp-save">Save & finish later</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
